import { useTranslation } from "react-i18next"

import type { PersonalPolicyConfirmationState } from "../hooks/use-personal-policy-confirmation"
import type { PersonalSpacesSource } from "@/api/admin-api"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"

type PersonalPolicyConfirmDialogProps = {
  state: PersonalPolicyConfirmationState
  onConfirm: () => void
  onDismiss: () => void
}

/**
 * FILTH holds a policy change that would hide more users than its guard applies without
 * a human OK (spec 3.7). The admin sees the number FILTH computed and confirms exactly
 * that number, or leaves the change unapplied. Without a number there is nothing to
 * confirm, so the dialog only explains that the previous settings stay in force.
 */
export function PersonalPolicyConfirmDialog({
  state,
  onConfirm,
  onDismiss,
}: PersonalPolicyConfirmDialogProps) {
  const { t } = useTranslation()
  const { expectedHidden, countChanged, policyChanged, pending } = state
  // A confirmation names the count AND the policy snapshot FILTH holds back; without both there is nothing to confirm.
  const canConfirm = expectedHidden !== null && Boolean(state.policyDigest)

  return (
    <AlertDialog
      open={state.open}
      onOpenChange={(open) => {
        if (!open) onDismiss()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(
              "adminDashboard.personalSources.policy.title",
              "Confirm before personal data is hidden"
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {canConfirm
              ? t(
                  "adminDashboard.personalSources.policy.description",
                  "This change hides the personal sources of {{count}} users from search at once, more than Prism applies without a human decision. Their data is deleted after the grace period unless access is restored in time.",
                  { count: expectedHidden }
                )
              : t(
                  "adminDashboard.personalSources.policy.noCount",
                  "Prism is holding this change because it would hide many users at once, but it did not report how many. It cannot be confirmed from here, and the previous settings stay in force."
                )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {countChanged && canConfirm && (
          <p className="text-sm font-medium" role="status">
            {t(
              "adminDashboard.personalSources.policy.countChanged",
              "The number changed to {{count}} while you were deciding. Check it before you confirm.",
              { count: expectedHidden }
            )}
          </p>
        )}
        {!countChanged && policyChanged && canConfirm && (
          <p className="text-sm font-medium" role="status">
            {t(
              "adminDashboard.personalSources.policy.policyChanged",
              "The policy changed since you reviewed it. Review the new effect and confirm again."
            )}
          </p>
        )}
        <p className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.personalSources.policy.keep",
            "If you do not confirm, your change stays saved but Prism keeps enforcing the previous settings, and this card shows the change as waiting."
          )}
        </p>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>
            {t("adminDashboard.personalSources.policy.cancel", "Keep the previous settings")}
          </AlertDialogCancel>
          {canConfirm && (
            <Button disabled={pending} variant="destructive" onClick={onConfirm}>
              {pending && <Spinner />}
              {t("adminDashboard.personalSources.policy.confirm", "Hide {{count}} users", {
                count: expectedHidden,
              })}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

type PersonalWindowWidenDialogProps = {
  open: boolean
  source: PersonalSpacesSource | null
  currentDays: number | null
  requestedDays: number | null
  pending: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Raising the history window indexes older items for every user of a source, which
 * costs storage and index time. It is never automatic (criterion 20): the admin says so
 * here, and only then does the request carry `widen_confirmed`.
 */
export function PersonalWindowWidenDialog({
  open,
  source,
  currentDays,
  requestedDays,
  pending,
  onConfirm,
  onCancel,
}: PersonalWindowWidenDialogProps) {
  const { t } = useTranslation()
  const sourceName = source ? t(`adminDashboard.personalSources.source.${source}`) : ""

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onCancel()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("adminDashboard.personalSources.widen.title", "Index older {{source}} history?", {
              source: sourceName,
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(
              "adminDashboard.personalSources.widen.description",
              "You are raising the history window from {{current}} to {{requested}} days. Prism will then index everything in that range for every user of this source, which raises storage and indexing cost. Lowering the window later removes the older items overnight.",
              { current: currentDays ?? "?", requested: requestedDays ?? "?" }
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>
            {t("adminDashboard.personalSources.widen.cancel", "Keep {{current}} days", {
              current: currentDays ?? "?",
            })}
          </AlertDialogCancel>
          <Button disabled={pending} onClick={onConfirm}>
            {pending && <Spinner />}
            {t("adminDashboard.personalSources.widen.confirm", "Widen to {{requested}} days", {
              requested: requestedDays ?? "?",
            })}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
